/**
 * The Pulse Card.
 *
 * A reading you can show someone: a nurse, a friend, a timeline. It is drawn on
 * a canvas here rather than assembled on a server, which is not an optimisation
 * — a server-rendered card would mean uploading the reading to render it, and
 * the entire promise of this app is that a reading does not leave the device
 * unless the person says so.
 *
 * The card includes the refusals. A "could not read you" card is the one worth
 * sharing most, because it is the evidence — and an app that only produces
 * shareable artefacts when it succeeds is quietly training everyone to believe
 * it always succeeds.
 */

const W = 1080;
const H = 1350; // 4:5 — the aspect most social surfaces crop to least badly

const PALETTE = {
  ground: '#0e0a0c',
  surface: '#181214',
  rule: '#3a2d31',
  ink: '#fcf8f7',
  muted: '#bfadb1',
  faint: '#a28f93',
  pulse: '#f87f94',
  signal: '#63ccab',
  warn: '#ebb673',
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Render the card.
 *
 * @param {object} reading  a finished scan result
 * @param {object} opts     { monkTone, swatch, strings }
 * @returns {Promise<Blob>}
 */
export async function renderPulseCard(reading, { monkTone, swatch, strings } = {}) {
  const s = { ...DEFAULT_STRINGS, ...(strings ?? {}) };
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = PALETTE.ground;
  ctx.fillRect(0, 0, W, H);

  const M = 84;
  const accent = reading.locked ? PALETTE.pulse : PALETTE.warn;

  /* ── wordmark ─────────────────────────────────────────────────────── */
  ctx.fillStyle = PALETTE.muted;
  ctx.font = '500 26px "IBM Plex Mono", monospace';
  ctx.fillText('OMNIALERA', M, M + 26);
  ctx.fillStyle = PALETTE.ink;
  ctx.font = '300 62px Georgia, serif';
  ctx.fillText('Ori', M, M + 100);

  // The mark, echoing the app icon.
  const mx = W - M - 44;
  const my = M + 62;
  for (let i = 3; i >= 1; i--) {
    ctx.beginPath();
    ctx.arc(mx, my, 44 * (1 + i * 0.36), 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(248,127,148,${0.34 - i * 0.08})`;
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.arc(mx, my, 44, 0, Math.PI * 2);
  ctx.fillStyle = PALETTE.pulse;
  ctx.fill();

  /* ── the number, or the refusal ───────────────────────────────────── */
  // Fixed anchors rather than a flowing layout. A card is a fixed canvas with
  // no scrollbar to rescue it, so text that grows — a long translation, a
  // wrapped headline — must grow into space reserved for it, never into the
  // block below.
  const RULE_Y = 590;
  const BODY_Y = 660;
  const CARD_Y = 780;

  if (reading.locked) {
    ctx.fillStyle = PALETTE.faint;
    ctx.font = '500 30px "IBM Plex Mono", monospace';
    ctx.fillText(s.pulse.toUpperCase(), M, 330);

    ctx.fillStyle = PALETTE.ink;
    ctx.font = '600 210px "IBM Plex Mono", monospace';
    const bpm = String(Math.round(reading.heartRateBpm));
    ctx.fillText(bpm, M, 520);

    const bpmWidth = ctx.measureText(bpm).width;
    ctx.fillStyle = PALETTE.muted;
    ctx.font = '400 42px "IBM Plex Mono", monospace';
    ctx.fillText(s.bpm, M + bpmWidth + 22, 520);
  } else {
    ctx.fillStyle = PALETTE.warn;
    ctx.font = '500 30px "IBM Plex Mono", monospace';
    ctx.fillText(s.refusedLabel.toUpperCase(), M, 330);

    ctx.fillStyle = PALETTE.ink;
    ctx.font = '300 84px Georgia, serif';
    wrapText(ctx, s.refusedHeadline, M, 430, W - 2 * M, 94, 2);
  }

  /* ── the line that makes the card worth existing ──────────────────── */
  ctx.strokeStyle = PALETTE.rule;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(M, RULE_Y);
  ctx.lineTo(W - M, RULE_Y);
  ctx.stroke();

  ctx.fillStyle = PALETTE.muted;
  ctx.font = '400 32px Georgia, serif';
  wrapText(ctx, reading.locked ? s.lockedBody : s.refusedBody, M, BODY_Y, W - 2 * M, 44, 3);

  /* ── the receipt ──────────────────────────────────────────────────── */
  const rows = [];
  if (reading.locked) {
    if (reading.breathsPerMinute) rows.push([s.breathing, `${Math.round(reading.breathsPerMinute)} ${s.perMin}`]);
    if (reading.variabilityMs) rows.push([s.variability, `${Math.round(reading.variabilityMs)} ms`]);
  }
  rows.push([s.time, `${reading.seconds.toFixed(1)} s`]);
  rows.push([s.regions, String(reading.contributingRegions ?? 0)]);
  if (reading.methodSpreadBpm !== null && reading.methodSpreadBpm !== undefined) {
    rows.push([s.methods, `${reading.methodSpreadBpm.toFixed(1)} ${s.bpm}`]);
  }
  if (monkTone) rows.push([s.tone, `${monkTone} / 10`]);

  const ROW_H = 52;
  const cardH = 40 + rows.length * ROW_H;
  // The footer sits below the receipt, not at a fixed offset from the bottom:
  // the receipt has a variable number of rows and must never be sat on.
  const FOOTER_Y = Math.max(CARD_Y + cardH + 74, H - 200);
  ctx.fillStyle = PALETTE.surface;
  roundRect(ctx, M, CARD_Y, W - 2 * M, cardH, 6);
  ctx.fill();
  ctx.strokeStyle = PALETTE.rule;
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.font = '400 29px "IBM Plex Mono", monospace';
  rows.forEach(([label, value], i) => {
    const ry = CARD_Y + 50 + i * ROW_H;
    ctx.fillStyle = PALETTE.faint;
    ctx.fillText(label, M + 34, ry);
    ctx.fillStyle = PALETTE.ink;
    ctx.textAlign = 'right';
    ctx.fillText(value, W - M - 34, ry);
    ctx.textAlign = 'left';
  });

  /* ── the swatch, because the tone is the point ────────────────────── */
  if (swatch) {
    ctx.fillStyle = swatch;
    ctx.fillRect(M, FOOTER_Y - 44, 56, 56);
    ctx.strokeStyle = PALETTE.rule;
    ctx.lineWidth = 2;
    ctx.strokeRect(M, FOOTER_Y - 44, 56, 56);
  }

  ctx.fillStyle = accent;
  ctx.font = '500 26px "IBM Plex Mono", monospace';
  wrapText(ctx, reading.locked ? s.trusted : s.honest, M + (swatch ? 80 : 0), FOOTER_Y, W - 2 * M - (swatch ? 80 : 0), 34, 2);

  ctx.fillStyle = PALETTE.faint;
  ctx.font = '400 24px "IBM Plex Mono", monospace';
  ctx.fillText(s.disclaimer, M, H - 70);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not render the card'))), 'image/png');
  });
}

/**
 * Draw wrapped text within a hard line budget.
 *
 * The budget is what keeps the layout honest: a translation that runs longer
 * than the English is truncated with an ellipsis rather than allowed to push
 * through the block below it. Losing the tail of a sentence is a small failure;
 * two overlapping paragraphs is a broken card.
 */
function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = Infinity) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const attempt = line ? `${line} ${word}` : word;
    if (ctx.measureText(attempt).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = attempt;
    }
  }
  if (line) lines.push(line);

  const shown = lines.slice(0, maxLines);
  if (lines.length > maxLines && shown.length) {
    let last = `${shown[shown.length - 1]}…`;
    while (ctx.measureText(last).width > maxWidth && last.length > 2) {
      last = `${last.slice(0, -2)}…`;
    }
    shown[shown.length - 1] = last;
  }

  shown.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return y + (shown.length - 1) * lineHeight;
}

const DEFAULT_STRINGS = {
  pulse: 'Pulse',
  bpm: 'bpm',
  breathing: 'Breathing',
  perMin: '/min',
  variability: 'Variability',
  time: 'Time taken',
  regions: 'Regions agreeing',
  methods: 'Method spread',
  tone: 'Skin tone',
  trusted: 'Confirmed across three regions and two methods',
  honest: 'Ori said so rather than guessing',
  refusedLabel: 'No reading',
  refusedHeadline: 'Ori could not see me clearly.',
  lockedBody:
    'Measured from light reflected off my skin — no watch, no cuff, nothing touching me. Ori only reports a number when independent regions and independent methods agree.',
  refusedBody:
    'Rather than print a confident wrong number, Ori refused — and recorded the refusal, so the failure can be measured and fixed instead of quietly repeating.',
  disclaimer: 'Prototype. Not a medical device. Not a diagnosis.',
};

/**
 * Hand the card to the person.
 *
 * Web Share with files where it exists (that is the phone path, and it opens
 * the OS sheet the person already knows), a download otherwise.
 */
export async function sharePulseCard(blob, { filename = 'ori-pulse-card.png', title = 'My Ori reading' } = {}) {
  const file = new File([blob], filename, { type: 'image/png' });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title });
      return 'shared';
    } catch (err) {
      // A cancelled share is a decision, not a failure — do not fall through
      // to a download the person just declined to make.
      if (err?.name === 'AbortError') return 'cancelled';
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
}
